'use client';

import type { Collection } from '@/app/actions/collection';
import type { AnswerType } from '~/model/AnswerType';
import type { ConclusionType } from '~/model/ConclusionType';
import type { ContributionType } from '~/model/ContributionType';
import type { InteractionType } from '~/model/InteractionType';
import type { SectionType } from '~/model/SectionType';
import { useCallback, useEffect, useState } from 'react';
import { fetchCollection, getResultsData } from '@/app/actions/server';
import { useApiErrorContext } from '@/app/components/Provider';
import { statusContribution } from '~/const/api';
import { useRequestState } from '~/hooks/request/useRequestState';
import { Results } from '../Results';
import { SurveyDefaultConclusion } from '../Survey/SurveyDefaultConclusion';
import { ResultsNavigation } from './ResultsNavigation';

export const ResultsWrapper = ({
  conclusions,
  interaction,
  results = false,
  sections,
}: {
  conclusions: Collection<ConclusionType>;
  interaction: InteractionType;
  results?: boolean;
  sections: Array<SectionType>;
}) => {
  const { setError } = useApiErrorContext();
  const { startRequest, handleError, handleSuccess } = useRequestState();

  const [answers, setAnswers] = useState<Array<AnswerType> | undefined>();
  const [comments, setComments] = useState<any>();
  const [questions, setQuestions] = useState<any>([]);
  const [response, setResponse] = useState<any>({ questions: [] });

  const fetchResultsData = useCallback(async () => {
    startRequest();

    try {
      if (!results) {
        const contributions = await fetchCollection<ContributionType>({
          pathname: `interactions/${interaction.id}/contributions`,
          revalidate: 0,
          searchParams: {
            'order[createdAt]': 'desc',
          },
          unpaginate: true,
        });

        const contributionFound = contributions?.['hydra:member']?.find(
          c => c.status === statusContribution.submitted,
        );

        if (contributionFound?.id) {
          const answersRes = await fetchCollection<AnswerType>({
            pathname: `contributions/${contributionFound.id}/answers`,
            revalidate: 0,
            unpaginate: true,
          });

          setAnswers(answersRes?.['hydra:member']);
        }
      }

      const { questions: q, comments: c, results: r } = await getResultsData(interaction.id);

      setQuestions(q);
      setComments(c);
      setResponse(r);

      handleSuccess();
    } catch (err: any) {
      handleError();
      setError(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interaction.id, results]);

  useEffect(() => {
    fetchResultsData();
  }, [fetchResultsData]);

  if (!interaction.result_visible_on_front) {
    return (
      <SurveyDefaultConclusion interaction={interaction} />
    );
  }

  return (
    <Results
      answers={answers}
      comments={comments}
      conclusions={conclusions['hydra:member']}
      interaction={interaction}
      questions={questions}
      results={response}
      sections={sections}
    >
      <ResultsNavigation id={interaction.id} />
    </Results>
  );
};
